import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAward,
  faClipboardList,
  faCodeBranch,
  faCreditCard,
  faGlobe,
  faLock,
  faPhone,
  faShieldHalved,
  faUser
} from '@fortawesome/pro-regular-svg-icons';

export type TabValue =
  | 'basic'
  | 'frontier'
  | 'password'
  | 'card'
  | '2fa'
  | 'phone'
  | 'loyalty'
  | 'reporting'
  | 'branchApprovers';

/** `section` = editable profile areas with Save; `accountAction` = separate flows (e.g. password reset). */
export type NavItemKind = 'section' | 'accountAction';

export type NavItemConfig = {
  value: TabValue;
  label: string;
  icon: IconDefinition;
  kind: NavItemKind;
  /** When true, nav entry is only shown in business (company) mode, not personal My Profile. */
  businessOnly?: boolean;
};

export const NAV_ITEMS: NavItemConfig[] = [
  { value: 'basic', label: 'Basic Details', icon: faUser, kind: 'section' },
  { value: 'frontier', label: 'Frontier Online', icon: faGlobe, kind: 'section' },
  { value: 'password', label: 'Reset Password', icon: faLock, kind: 'accountAction' },
  { value: 'card', label: 'Card on File', icon: faCreditCard, kind: 'section' },
  { value: '2fa', label: 'Two Factor Auth', icon: faShieldHalved, kind: 'section' },
  { value: 'phone', label: 'Phone Numbers', icon: faPhone, kind: 'section' },
  { value: 'loyalty', label: 'Loyalty Numbers', icon: faAward, kind: 'section' },
  {
    value: 'reporting',
    label: 'Reporting Reqs.',
    icon: faClipboardList,
    kind: 'section',
    businessOnly: true
  },
  {
    value: 'branchApprovers',
    label: 'Branch & Approvers',
    icon: faCodeBranch,
    kind: 'section',
    businessOnly: true
  }
];

export const NAV_SECTION_ITEMS = NAV_ITEMS.filter((i) => i.kind === 'section');
export const NAV_ACCOUNT_ITEMS = NAV_ITEMS.filter((i) => i.kind === 'accountAction');

/** Query key for deep-linking to a tab on the refactored profile page (e.g. `?section=card`). */
export const profileSectionSearchParam = 'section' as const;

export function parseProfileSectionTabParam(value: string | null): TabValue | null {
  if (!value) return null;
  const found = NAV_ITEMS.find((i) => i.value === value);
  return found ? found.value : null;
}

export function profileRefactorSectionSearch(section: TabValue): string {
  return `?${profileSectionSearchParam}=${encodeURIComponent(section)}`;
}

export const NAV_SECTION_SWITCH_LOCK_TOOLTIP =
  "The current section you're editing has unsaved changes. Use Save or Cancel before switching to another section.";

export const NAV_SECTION_URL_LOCK_TOAST =
  'Save or cancel changes on the current section before switching via link or bookmark.';

export const NAV_SELECT_TRAVELER_TOOLTIP = 'Select a traveler to edit profile sections.';
